// Core
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
// Validity dates use the same range selector as the application header.
import { Button, FieldSelect, FieldText, Modal, ValidityDateRangePicker } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  openNotificationPopup,
  openSuccessAlert,
  selectThemeMode,
} from "state/application/applicationSlice";
import {
  useDeleteUserMutation,
  useLinkUserToCommunityMutation,
  useUnlinkUserFromCommunityMutation,
  useUpdateUserByIdMutation,
  useUpdateUserRoleMutation,
} from "state/admin/adminApiSlice";
import { useResetPasswordRequestMutation } from "state/auth/authApiSlice";

// Utils
import { avColor, initials } from "utils/avatar";
import { defaultBusinessEndDate, lisbonToday } from "utils/businessValidity";

const ROLE_META = {
  SUPER_USER: {
    icon: "shield",
    chip: { bg: "#ede7f6", fg: "#6a1b9a" },
    chipDark: { bg: "#3b1f6e", fg: "#ce93d8" },
  },
  ADMIN: {
    icon: "manage_accounts",
    chip: { bg: "#e0f7fa", fg: "#006064" },
    chipDark: { bg: "#0e3a4f", fg: "#4dd0e1" },
  },
  MEMBER: {
    icon: "person",
    chip: { bg: "#e8f5e9", fg: "#2e7d32" },
    chipDark: { bg: "#1e2d1e", fg: "#81c784" },
  },
};

function RoleChip({ role, isDark }) {
  const { t } = useTranslation(["admin"]);
  const meta = ROLE_META[role] || ROLE_META.MEMBER;
  const c = isDark ? meta.chipDark : meta.chip;
  return (
    <span
      className="text-[11px] font-bold"
      style={{ background: c.bg, color: c.fg, padding: "3px 9px", borderRadius: 7 }}
    >
      {t(`users.roles.${role}`, { defaultValue: role })}
    </span>
  );
}

RoleChip.propTypes = {
  role: PropTypes.string.isRequired,
  isDark: PropTypes.bool.isRequired,
};

function matchesSearch(user, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    (user.name || "").toLowerCase().includes(q) || (user.email || "").toLowerCase().includes(q)
  );
}

/* ── User detail modal ──────────────────────────────────────────────────── */
function UserDetailModal({ user, communities, isDark, onClose, onDelete }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(["admin", "common"]);

  const [localName, setLocalName] = useState(user.name ?? "");
  const [localEmail, setLocalEmail] = useState(user.email ?? "");
  const [selectedCommunityId, setSelectedCommunityId] = useState("");
  const [selectedRole, setSelectedRole] = useState("MEMBER");
  const [membershipStartDate, setMembershipStartDate] = useState(lisbonToday());
  const [membershipEndDate, setMembershipEndDate] = useState(() =>
    defaultBusinessEndDate(lisbonToday())
  );

  const [updateUserById, { isLoading: isSaving }] = useUpdateUserByIdMutation();
  const [resetPasswordRequest, { isLoading: isSendingReset }] = useResetPasswordRequestMutation();
  const [updateUserRole, { isLoading: isRoleUpdating }] = useUpdateUserRoleMutation();
  const [linkUserToCommunity, { isLoading: isLinking }] = useLinkUserToCommunityMutation();
  const [unlinkUserFromCommunity] = useUnlinkUserFromCommunityMutation();

  const assignedCommunities = user.community_roles ?? [];
  const assignedIds = new Set(assignedCommunities.map((c) => c.id));
  const communityOptions = (communities ?? [])
    .filter((c) => !assignedIds.has(c.id))
    .map((c) => ({ value: c.id, label: c.name }));

  const notifyError = (err) =>
    dispatch(openNotificationPopup({ notification: err, type: "error" }));

  const handleSaveProfile = async () => {
    try {
      await updateUserById({
        userId: user.id,
        data: { name: localName, email: localEmail },
      }).unwrap();
      dispatch(
        openSuccessAlert({
          title: t("users.role_updated_title"),
          description: t("users.profile_updated_ok"),
        })
      );
    } catch (err) {
      notifyError(err);
    }
  };

  const handleSendPasswordReset = async () => {
    try {
      await resetPasswordRequest({ email: user.email }).unwrap();
      dispatch(
        openNotificationPopup({
          notification: { message: t("users.password_reset_sent") },
          type: "success",
        })
      );
    } catch (err) {
      notifyError(err);
    }
  };

  const handleRoleChange = async (newRole) => {
    try {
      await updateUserRole({ userId: user.id, role: newRole }).unwrap();
      dispatch(
        openSuccessAlert({
          title: t("users.role_updated_title"),
          description: t("users.role_updated_ok"),
        })
      );
    } catch (err) {
      notifyError(err);
    }
  };

  const handleAddCommunity = async () => {
    if (!selectedCommunityId) return;
    if (!membershipStartDate || (membershipEndDate && membershipEndDate < membershipStartDate)) {
      notifyError(
        t("users.invalid_validity", {
          defaultValue: "The validity end must be on or after its start.",
        })
      );
      return;
    }
    try {
      await linkUserToCommunity({
        userId: user.id,
        communityId: selectedCommunityId,
        role: selectedRole,
        startDate: membershipStartDate,
        endDate: membershipEndDate || undefined,
      }).unwrap();
      setSelectedCommunityId("");
      dispatch(
        openSuccessAlert({
          title: t("users.community_added_title"),
          description: t("users.community_added_ok"),
        })
      );
    } catch (err) {
      notifyError(err);
    }
  };

  const handleRemoveCommunity = async (communityId) => {
    try {
      await unlinkUserFromCommunity({
        userId: user.id,
        communityId,
        effectiveAt: membershipStartDate,
      }).unwrap();
      dispatch(
        openSuccessAlert({
          title: t("users.community_removed_title"),
          description: t("users.community_removed_ok"),
        })
      );
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      maxWidth={500}
      footer={
        <>
          <button
            type="button"
            className="rec-btn-ghost mr-auto"
            style={{ color: "var(--danger)" }}
            onClick={() => onDelete(user)}
          >
            <Icon name="delete_outline" />
            {t("users.delete_confirm_btn")}
          </button>
          <Button variant="ghost" onClick={onClose}>
            {t("users.close")}
          </Button>
          <Button disabled={isSaving} onClick={handleSaveProfile}>
            <Icon name="check" />
            {t("users.save_profile")}
          </Button>
        </>
      }
    >
      <div className="flex items-center gap-3 mb-[18px]">
        <div
          className="rec-av-sm"
          style={{ background: avColor(user.name), width: 40, height: 40, fontSize: 15 }}
        >
          {initials(user.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[16px] font-bold text-ink">{user.name}</div>
          <div className="text-[12.5px] text-ink-3">{user.email}</div>
        </div>
        <RoleChip role={user.role?.name} isDark={isDark} />
      </div>

      <div className="flex flex-col gap-[14px]">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
          <FieldText label={t("form.name")} value={localName} onChange={setLocalName} />
          <FieldText
            label={t("form.email_required")}
            type="email"
            value={localEmail}
            onChange={setLocalEmail}
          />
        </div>
        <FieldSelect
          label={t("users.col_role")}
          value={user.role?.name ?? ""}
          options={Object.keys(ROLE_META).map((r) => ({
            value: r,
            label: t(`users.roles.${r}`, { defaultValue: r }),
          }))}
          onChange={handleRoleChange}
          disabled={isRoleUpdating}
        />

        <div className="rec-field">
          <span className="text-[12.5px] font-semibold text-ink-2">
            {t("users.communities_label")}
          </span>
          <div className="flex flex-wrap gap-[6px] mb-1">
            {assignedCommunities.length === 0 ? (
              <span className="text-[12.5px] text-ink-3">{t("users.no_communities")}</span>
            ) : (
              assignedCommunities.map((c) => (
                <span
                  key={c.id}
                  className={`rec-chip ${c.role === "ADMIN" ? "rec-chip-amber" : "rec-chip-teal"}`}
                >
                  {c.name}
                  <span className="opacity-70">
                    · {c.role === "ADMIN" ? t("users.role_admin") : t("users.role_member")}
                  </span>
                  <button
                    type="button"
                    className="bg-transparent border-0 p-0 cursor-pointer text-inherit inline-flex"
                    title={t("users.cancel")}
                    onClick={() => handleRemoveCommunity(c.id)}
                  >
                    <Icon name="close" style={{ fontSize: 13 }} />
                  </button>
                </span>
              ))
            )}
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <FieldSelect
                value={selectedCommunityId}
                options={[
                  { value: "", label: t("users.add_community_select") },
                  ...communityOptions,
                ]}
                onChange={setSelectedCommunityId}
              />
            </div>
            <div className="w-[130px] shrink-0">
              <FieldSelect
                value={selectedRole}
                options={[
                  { value: "MEMBER", label: t("users.role_member") },
                  { value: "ADMIN", label: t("users.role_admin") },
                ]}
                onChange={setSelectedRole}
              />
            </div>
            <div className="w-[300px] shrink-0">
              <ValidityDateRangePicker
                value={{ start: membershipStartDate, end: membershipEndDate }}
                onChange={({ start, end }) => {
                  setMembershipStartDate(start);
                  setMembershipEndDate(end);
                }}
              />
            </div>
            <Button
              variant="ghost"
              disabled={!selectedCommunityId || isLinking}
              onClick={handleAddCommunity}
            >
              <Icon name="add" />
              {t("users.add_community_btn")}
            </Button>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          disabled={isSendingReset || !user.email}
          onClick={handleSendPasswordReset}
        >
          <Icon name="lock_reset" />
          {t("users.send_password_reset")}
        </Button>
      </div>
    </Modal>
  );
}

UserDetailModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  user: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  communities: PropTypes.array.isRequired,
  isDark: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

/* ── Users section (design's user-cards grid) ───────────────────────────── */
function UsersSection({ users, communities }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(["admin"]);
  const themeMode = useSelector(selectThemeMode);
  const isDark = themeMode === "dark";

  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(1);
  const [deleteDialogUser, setDeleteDialogUser] = useState(null);
  const [detailUserId, setDetailUserId] = useState(null);

  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

  const filteredUsers = useMemo(
    () =>
      (users ?? [])
        .filter((u) => matchesSearch(u, searchQuery))
        .filter((u) => roleFilter === "" || u.role?.name === roleFilter),
    [users, searchQuery, roleFilter]
  );

  const USERS_PER_PAGE = 12;
  const pages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));
  const safePage = Math.min(page, pages);
  const pageUsers = filteredUsers.slice((safePage - 1) * USERS_PER_PAGE, safePage * USERS_PER_PAGE);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, roleFilter]);

  const detailUser = detailUserId ? users.find((u) => u.id === detailUserId) : null;

  const handleDelete = async () => {
    try {
      await deleteUser(deleteDialogUser.id).unwrap();
      dispatch(
        openSuccessAlert({
          title: t("users.deleted_title"),
          description: t("users.deleted_ok"),
        })
      );
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
    setDeleteDialogUser(null);
    setDetailUserId(null);
  };

  return (
    <div className="rec-card">
      {/* header */}
      <div
        className="flex items-center gap-3"
        style={{ padding: "18px var(--pad-card) 14px", borderBottom: "1px solid var(--border)" }}
      >
        <div className="rec-kpi-ic rec-tint-info" style={{ width: 36, height: 36 }}>
          <Icon name="group" fill style={{ fontSize: 20 }} />
        </div>
        <div>
          <div className="text-[15px] font-bold text-ink">{t("users.title")}</div>
          <div className="text-[12px] text-ink-3">{t("users.subtitle")}</div>
        </div>
      </div>

      {/* toolbar */}
      <div className="rec-toolbar">
        <div className="rec-input-wrap flex-1 max-w-[360px]">
          <Icon name="search" />
          <input
            className="rec-input"
            placeholder={t("users.search_placeholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="grow" />
        {Object.entries(ROLE_META).map(([role, meta]) => (
          <button
            key={role}
            type="button"
            className={`rec-filter-ic${roleFilter === role ? " on" : ""}`}
            title={t(`users.roles.${role}`, { defaultValue: role })}
            onClick={() => setRoleFilter(roleFilter === role ? "" : role)}
          >
            <Icon name={meta.icon} style={{ fontSize: 19 }} />
          </button>
        ))}
      </div>

      {/* user cards */}
      <div
        className="grid gap-3"
        style={{
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          padding: "var(--pad-card)",
        }}
      >
        {pageUsers.map((u) => (
          <div
            key={u.id}
            className="rec-card rec-card-pad !shadow-none relative cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={() => setDetailUserId(u.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setDetailUserId(u.id);
            }}
          >
            <button
              type="button"
              className="rec-icon-btn !w-[30px] !h-[30px] absolute top-2 right-2"
              style={{ color: "var(--danger)" }}
              title={t("users.delete_confirm_btn")}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteDialogUser(u);
              }}
            >
              <Icon name="delete" style={{ fontSize: 17 }} />
            </button>
            <div className="flex items-start gap-[11px] pr-[30px]">
              <div
                className="rec-av-sm"
                style={{ background: avColor(u.name), width: 38, height: 38, fontSize: 14 }}
              >
                {initials(u.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold text-ink whitespace-nowrap overflow-hidden text-ellipsis">
                  {u.name}
                </div>
                <div className="text-[11.5px] text-ink-3 whitespace-nowrap overflow-hidden text-ellipsis">
                  {u.email}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <RoleChip role={u.role?.name} isDark={isDark} />
              <span className="text-[11.5px] text-ink-3">
                {u.community_roles?.length
                  ? t("users.communities_count", { count: u.community_roles.length })
                  : "—"}
              </span>
            </div>
          </div>
        ))}
        {filteredUsers.length === 0 && (
          <div
            className="text-center text-[13px] text-ink-3"
            style={{ gridColumn: "1/-1", padding: 40 }}
          >
            {t("users.search_placeholder")}
          </div>
        )}
      </div>

      {/* footer pager (design's tbl-foot) */}
      {filteredUsers.length > 0 && (
        <div className="rec-tbl-foot" style={{ borderTop: "1px solid var(--border)" }}>
          <span className="text-[13px] text-ink-3">
            {t("users.showing", { shown: pageUsers.length, total: filteredUsers.length })}
          </span>
          <div className="rec-pager">
            <button
              type="button"
              disabled={safePage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label={t("users.prev_page")}
            >
              <Icon name="chevron_left" style={{ fontSize: 18 }} />
            </button>
            {Array.from({ length: Math.min(pages, 5) }).map((_, i) => {
              const base = Math.min(Math.max(1, safePage - 2), Math.max(1, pages - 4));
              const n = base + i;
              return n <= pages ? (
                <button
                  key={n}
                  type="button"
                  className={safePage === n ? "on" : ""}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ) : null;
            })}
            <button
              type="button"
              disabled={safePage === pages}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              aria-label={t("users.next_page")}
            >
              <Icon name="chevron_right" style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
      )}

      {/* detail modal */}
      {detailUser && (
        <UserDetailModal
          key={detailUser.id}
          user={detailUser}
          communities={communities}
          isDark={isDark}
          onClose={() => setDetailUserId(null)}
          onDelete={(u) => setDeleteDialogUser(u)}
        />
      )}

      {/* delete confirm */}
      <Modal
        open={!!deleteDialogUser}
        onClose={() => setDeleteDialogUser(null)}
        title={t("users.delete_confirm_title")}
        subtitle={
          deleteDialogUser ? t("users.delete_confirm_body", { name: deleteDialogUser.name }) : ""
        }
        maxWidth={420}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteDialogUser(null)}>
              {t("users.cancel")}
            </Button>
            <button
              type="button"
              className="rec-btn-danger"
              disabled={isDeleting}
              onClick={handleDelete}
            >
              <Icon name="delete_forever" />
              {t("users.delete_confirm_btn")}
            </button>
          </>
        }
      />
    </div>
  );
}

UsersSection.defaultProps = {
  users: [],
  communities: [],
};

UsersSection.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  users: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  communities: PropTypes.array,
};

export default UsersSection;
